import { useApiClient } from "@/src/hooks/useApiClient";
import { useQueryErrorToast } from "@/src/hooks/useQueryErrorToast";
import { useTranslations } from "@/src/i18n/i18n";
import type { PostFilters } from "@/src/pages/Posts/hooks/usePostFilters";
import { PAGE_SIZE } from "@/src/pages/Posts/posts.type";

/**
 * Le fil vu par l'administration, filtré et paginé **par le serveur**.
 *
 * ## Une liste, dont la file de validation est un filtre
 *
 * `GET /moderation/post` rend les **quatre** statuts quand `status` est absent —
 * publications retirées par leur auteur comprises. La file (`pending_review`)
 * n'est qu'un cas particulier : c'est la même route, le même écran, un filtre de
 * plus. Sans cela, tout ce que le classifieur laisse passer restait invisible du
 * back-office, c'est-à-dire l'essentiel du fil.
 *
 * ## La pagination diffère de celle du fil mobile, et c'est délibéré
 *
 * `offset` + `total`, là où l'application pagine par curseur. « 42 en attente »
 * est exactement le nombre qui dit s'il faut s'y mettre, et le volume d'une file
 * de modération rend le `COUNT(*)` sans conséquence. Le curseur du fil répond à
 * un problème qui ne se pose pas ici — on ne publie pas pendant qu'un modérateur
 * travaille.
 *
 * ⚠️ **Ne reprends pas le motif d'`eventList.ts`**, qui filtre et pagine dans le
 * navigateur : c'était un contournement de l'absence de route d'administration
 * des événements. Ici la route existe, filtre et pagine.
 */
export function useModerationPosts(filters: PostFilters) {
  const $api = useApiClient();
  const t = useTranslations("posts");

  /*
    ⚠️ `isLoading`, **jamais `isFetching`** : ce dernier repasse à vrai à chaque
    rafraîchissement d'arrière-plan, y compris celui qu'une action déclenche.
    La liste basculerait alors sur des squelettes juste après chaque publication
    tranchée — soit à peu près en permanence sur cet écran.
  */
  const { data, error, isError, isLoading } = $api.useQuery(
    "get",
    "/api/v1/moderation/post",
    {
      params: {
        query: {
          // `all` n'est pas une valeur du serveur : `status` absent signifie
          // déjà « les quatre ».
          ...(filters.status === "all" ? {} : { status: filters.status }),
          ...(filters.type === "all" ? {} : { type: filters.type }),
          deleted: filters.deleted,
          sort: filters.sort,
          order: filters.order,
          ...(filters.q.trim() ? { q: filters.q.trim() } : {}),
          limit: PAGE_SIZE,
          offset: (filters.page - 1) * PAGE_SIZE,
        },
      },
    },
  );

  useQueryErrorToast(isError, t("list-error"), error);

  const total = data?.data?.total ?? 0;

  return {
    posts: data?.data?.posts ?? [],
    total,
    pageCount: Math.max(1, Math.ceil(total / PAGE_SIZE)),
    isLoading,
    isError,
  };
}
